/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';
import propTypes from 'prop-types';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import shortid from 'shortid';

import { SEARCH_CONTEXTS } from 'app/Search/const';
import Search from 'app/Search/container';
import { utcToPtDateString } from 'lib/date';
import { noop } from 'lib/noop';
import { ALL_SITES, HAUTE_LOOK, NORDSTROM, NORDSTROM_RACK } from 'lib/const';
import { Arrow } from 'ui/Components';

import styles from './SearchTab.css';

/**
 * This component is the collection of component shown in Explorer->Component sections/drawer.
 * - @param {object} component selected component to disable/enable the tab.
 * - @param {object} getExistingComponent Component action to be dispatched
 * @returns {React.Component} the components tab component.
 */
class SearchTab extends React.Component {
  static propTypes = {
    addFilter: propTypes.func,
    checkStatusAndVisitPage: propTypes.func,
    componentsList: propTypes.array,
    createNewUnsavedPage: propTypes.func,
    cwdSiteRoot: propTypes.string,
    deleteFilter: propTypes.func,
    duplicatePage: propTypes.func,
    filters: propTypes.arrayOf(
      propTypes.shape({
        text: propTypes.string,
        key: propTypes.string,
        val: propTypes.oneOfType([propTypes.string, propTypes.array])
      })
    ),
    hidePageSelection: propTypes.bool,
    isContainer: propTypes.bool,
    onFinish: propTypes.func,
    onResultClick: propTypes.func,
    openForEditing: propTypes.bool,
    storeContext: propTypes.func,
    storeResultPageNum: propTypes.func
  };

  static defaultProps = {
    addFilter: noop,
    checkStatusAndVisitPage: noop,
    componentsList: [],
    createNewUnsavedPage: noop,
    cwdSiteRoot: '',
    deleteFilter: noop,
    duplicatePage: noop,
    filters: [],
    hidePageSelection: false,
    isContainer: false,
    onFinish: undefined,
    onResultClick: undefined,
    openForEditing: false,
    storeContext: noop,
    storeResultPageNum: noop
  };

  constructor(props) {
    super(props);
    this.state = {
      component: true || this.props.hidePageSelection,
      dynamic: 'false',
      endDate: '',
      isAdvancedOpen: false,
      page: false,
      route: '',
      sites: [this.props.cwdSiteRoot],
      startDate: '',
      type: '',
      userId: ''
    };
    this.applyFilters = this.applyFilters.bind(this);
    this.clearAllFilters = this.clearAllFilters.bind(this);
    this.closeAdvancedTab = this.closeAdvancedTab.bind(this);
    this.handleRoute = this.handleRoute.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleUserId = this.handleUserId.bind(this);
    this.toggleAdvanced = this.toggleAdvanced.bind(this);
    this.toggleComponentType = this.toggleComponentType.bind(this);
    this.toggleContext = this.toggleContext.bind(this);
    this.toggleDynamic = this.toggleDynamic.bind(this);
    this.toggleSite = this.toggleSite.bind(this);
  }

  handleUserId(event) {
    const text = event.target.value;
    this.setState({ userId: text.toUpperCase() });
  }

  handleRoute(event) {
    const route = event.target.value;
    this.setState({ route });
  }

  handleSelect(event) {
    const type = event.target.name;
    const val = event.target.value;

    if (type === 'start') {
      this.setState({ startDate: utcToPtDateString(val, 'YYYY-MM-DD') });
    } else if (type === 'end') {
      this.setState({ endDate: utcToPtDateString(val, 'YYYY-MM-DD') });
    }
  }

  toggleContext(event) {
    const { storeContext } = this.props;
    const val = event.target.value;
    const opposite = val === 'component' ? 'page' : 'component';
    const newState = {
      [val]: true,
      [opposite]: false,
      endDate: '',
      dynamic: 'false',
      isAdvancedOpen: true,
      sites: [this.props.cwdSiteRoot],
      startDate: '',
      type: '',
      userId: ''
    };
    this.setState(newState, () => storeContext(val === 'page' ? 'pageOrFolder' : val));
  }

  toggleDynamic(event) {
    const val = event.target.value;
    if (val === 'dynamic-yes') {
      this.setState({ dynamic: 'true' });
    } else if (val === 'dynamic-no') {
      this.setState({ dynamic: 'false' });
    }
  }

  toggleSite(event) {
    const val = event.target.value;

    if (this.state.sites.includes(val)) {
      this.setState(state => {
        return { sites: state.sites.filter(site => site !== val) };
      });
    } else {
      this.setState(state => {
        return { sites: state.sites.concat(val) };
      });
    }
  }

  toggleComponentType(event) {
    const val = event.target.value;
    this.setState({
      type: val
    });
  }

  toggleAdvanced() {
    const curr = this.state.isAdvancedOpen;
    if (curr) {
      this.setState({ isAdvancedOpen: false });
    } else {
      this.setState({ isAdvancedOpen: true });
    }
  }

  closeAdvancedTab() {
    this.setState({ isAdvancedOpen: false });
  }

  clearAllFilters() {
    this.setState({
      dynamic: 'false',
      endDate: '',
      userId: '',
      isAdvancedOpen: false,
      route: '',
      sites: [this.props.cwdSiteRoot],
      startDate: '',
      type: ''
    });
  }

  applyFilters() {
    const { addFilter, deleteFilter, filters } = this.props;
    const { component, dynamic, endDate, route, startDate, sites, type, userId } = this.state;
    const context = component ? 'component' : 'pageOrFolder';
    const simpleFilters = [];

    if (userId !== '') {
      filters.forEach(filter => {
        if (filter.key === 'Tags.UserTags' && !ALL_SITES.includes(filter.val)) {
          deleteFilter(context, filter.key, filter.val);
        }
      });
      simpleFilters.push({
        k: 'Tags.UserTags',
        v: userId
      });
      addFilter(context, 'Tags.UserTags', userId);
    }

    if (sites.length) {
      filters.forEach(filter => {
        if (filter.key === 'Tags.UserTags' && ALL_SITES.includes(filter.val)) {
          deleteFilter(context, filter.key, filter.val);
        }
      });
      sites.forEach(site => {
        simpleFilters.push({
          k: 'Tags.UserTags',
          v: site
        });
        addFilter(context, 'Tags.UserTags', site);
      });
    }

    if (
      startDate !== '' &&
      endDate !== '' &&
      startDate !== 'Invalid Date' &&
      endDate !== 'Invalid date'
    ) {
      filters.forEach(
        filter => filter.key === 'DateCreated' && deleteFilter(context, filter.key, filter.val)
      );
      simpleFilters.push({
        k: 'DateCreated',
        v: startDate.concat('--', endDate)
      });
      addFilter(context, 'DateCreated', startDate.concat('--', endDate));
    }
    if (component) {
      if (type !== '') {
        filters.forEach(
          filter =>
            filter.key === 'ComponentType' && deleteFilter(context, filter.key, filter.val)
        );
        simpleFilters.push({
          k: 'ComponentType',
          v: type
        });
        addFilter('component', 'ComponentType', type);
      }
      if (dynamic === 'true') {
        filters.forEach(
          filter => filter.key === 'IsDynamic' && deleteFilter(context, filter.key, filter.val)
        );
        simpleFilters.push({
          k: 'IsDynamic',
          v: 'true'
        });
        addFilter('component', 'IsDynamic', 'true');
      } else if (dynamic === 'false') {
        filters.forEach(
          filter => filter.key === 'IsDynamic' && deleteFilter(context, filter.key, filter.val)
        );
        simpleFilters.push({
          k: 'IsDynamic',
          v: 'false'
        });
        addFilter('component', 'IsDynamic', 'false');
      }
    } else if (route !== '') {
      simpleFilters.push({
        k: 'Routes',
        v: route
      });
      addFilter(context, 'Routes', route);
    }
    this.closeAdvancedTab();
  }

  render() {
    const {
      checkStatusAndVisitPage,
      componentsList,
      createNewUnsavedPage,
      duplicatePage,
      hidePageSelection,
      isContainer,
      onFinish,
      onResultClick,
      openForEditing,
      storeResultPageNum
    } = this.props;

    const {
      component,
      dynamic,
      endDate,
      isAdvancedOpen,
      page,
      route,
      sites,
      startDate,
      type,
      userId
    } = this.state;

    const context = component ? 'component' : 'pageOrFolder';

    // Need to set styles due to reactstrap not allowing overrides of built-in styles through css
    const searchTabButtonStyle = {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      color: '#2c2c2c',
      display: 'flex',
      width: '150px',
      height: '25px',
      alignContent: 'center',
      justifyContent: 'space-between'
    };

    return (
      <Form>
        {!hidePageSelection && (
          <div className={styles.topSpacing}>
            <FormGroup className={styles.formGroup}>
              <Input
                type="radio"
                className={styles.context}
                value="page"
                id="page-radio-btn"
                data-test-id="searchTab-page"
                checked={page}
                onChange={this.toggleContext}
              />
              <Label className={styles.context} for="page-radio-btn">
                Page
              </Label>
            </FormGroup>
            <FormGroup>
              <Input
                type="radio"
                className={styles.context}
                value="component"
                id="component-radio-btn"
                data-test-id="searchTab-component"
                checked={component}
                onChange={this.toggleContext}
              />
              <Label for="component-radio-btn" className={styles.context}>
                Component
              </Label>
            </FormGroup>
          </div>
        )}
        <div className={styles.advanced}>
          <Button
            type="button"
            data-test-id="searchTab-advanced"
            onClick={this.toggleAdvanced}
            style={searchTabButtonStyle}
          >
            Advanced Filters
            <Arrow facing={isAdvancedOpen ? 'down' : 'right'} />
          </Button>
        </div>
        {isAdvancedOpen && (
          <div className={styles.hardFilters}>
            <FormGroup className={styles.userId}>
              <Label for="userId-input">User ID:</Label>
              <Input
                type="text"
                name="userId"
                id="userId-input"
                data-test-id="searchTab-userId"
                className={styles.userIdInput}
                onChange={this.handleUserId}
                value={userId}
              />
            </FormGroup>
            <div className={styles.sites}>
              <p>Site:</p>
              <div>
                <FormGroup className={styles.sitesOption}>
                  <Input
                    type="checkbox"
                    value={NORDSTROM}
                    id="nordstrom"
                    data-test-id="searchTab-site-nordstrom"
                    checked={sites.includes(NORDSTROM)}
                    onChange={this.toggleSite}
                  />
                  <Label for="nordstrom">{NORDSTROM}</Label>
                </FormGroup>
                <FormGroup className={styles.sitesOption}>
                  <Input
                    type="checkbox"
                    value={NORDSTROM_RACK}
                    id="nr"
                    data-test-id="searchTab-site-nr"
                    checked={sites.includes(NORDSTROM_RACK)}
                    onChange={this.toggleSite}
                  />
                  <Label for="nr">{NORDSTROM_RACK}</Label>
                </FormGroup>
                <FormGroup className={styles.sitesOption}>
                  <Input
                    type="checkbox"
                    value={HAUTE_LOOK}
                    id="hl"
                    data-test-id="searchTab-site-hl"
                    checked={sites.includes(HAUTE_LOOK)}
                    onChange={this.toggleSite}
                  />
                  <Label for="hl">{HAUTE_LOOK}</Label>
                </FormGroup>
              </div>
            </div>
            <div className={styles.filterSpacing} id={styles.calendarColumn}>
              Date Created:
              <FormGroup className={styles.dateFormatting}>
                <Label for="date-range-start">Range Start:</Label>
                <Input
                  type="date"
                  name="start"
                  id="date-range-start"
                  data-test-id="searchTab-range-start"
                  value={startDate}
                  className={styles.dateRange}
                  onChange={this.handleSelect}
                />
              </FormGroup>
              <FormGroup className={styles.dateFormatting}>
                <Label for="date-range-end">Range End:</Label>
                <Input
                  type="date"
                  name="end"
                  id="date-range-end"
                  data-test-id="searchTab-range-end"
                  value={endDate}
                  onChange={this.handleSelect}
                />
              </FormGroup>
            </div>
            {component ? (
              <div>
                <div className={styles.dynamicContainer}>
                  <p>Is Dynamic?</p>
                  <FormGroup className={styles.dynamic}>
                    <Input
                      type="radio"
                      value="dynamic-yes"
                      id="dynamic-yes-radio"
                      data-test-id="searchTab-dynamic-yes"
                      onChange={this.toggleDynamic}
                      checked={dynamic === 'true'}
                    />
                    <Label for="dynamic-yes-radio">Yes</Label>
                  </FormGroup>
                  <FormGroup className={styles.dynamic}>
                    <Input
                      type="radio"
                      value="dynamic-no"
                      id="dynamic-no-radio"
                      data-test-id="searchTab-dynamic-no"
                      onChange={this.toggleDynamic}
                      checked={dynamic === 'false'}
                    />
                    <Label for="dynamic-no-radio">No</Label>
                  </FormGroup>
                </div>
                <FormGroup className={styles.filterSpacing}>
                  <Label for="component-type-dropdown">Component Type:</Label>
                  <select
                    name="SchemaName"
                    id="component-type-dropdown"
                    data-test-id="searchTab-component-type"
                    onChange={this.toggleComponentType}
                    className={styles.dropdown}
                    value={type}
                  >
                    <option value="" />
                    {componentsList.map(component => (
                      <option key={shortid.generate()} value={component}>
                        {component}
                      </option>
                    ))}
                  </select>
                </FormGroup>
              </div>
            ) : (
              <FormGroup className={styles.route}>
                <Label for="route-input">Route:</Label>
                <Input
                  data-test-id="searchTab-route"
                  id="route-input"
                  name="route"
                  onChange={this.handleRoute}
                  placeholder="e.g. /browse/women"
                  type="text"
                  value={route}
                />
              </FormGroup>
            )}
            <div className={styles.applyAndClear}>
              <button
                type="button"
                data-test-id="searchTab-apply-filters"
                onClick={() => {
                  this.applyFilters();
                  storeResultPageNum(context, 1);
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
        <Search
          checkStatusAndVisitPage={checkStatusAndVisitPage}
          clearSimpleFilters={this.clearAllFilters}
          componentTypeSelected={this.state.type}
          context={component ? SEARCH_CONTEXTS.COMPONENT : SEARCH_CONTEXTS.PAGE_OR_FOLDER}
          createNewUnsavedPage={createNewUnsavedPage}
          createSearchUrl
          duplicatePage={duplicatePage}
          dynamicSelected={dynamic}
          endDateSelected={this.state.endDate}
          hidePageAndSelectComponent={hidePageSelection}
          isContainer={isContainer}
          onFinish={onFinish}
          onResultClick={onResultClick}
          openForEditing={openForEditing}
          startDateSelected={this.state.startDate}
          userIdSelected={this.state.userId}
        />
      </Form>
    );
  }
}

export default SearchTab;
